import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { Ic } from '../components/ui'
import { apiUrl } from '../shared/config/apiBase'

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

/* ─── Input ─── */
function Field({ label, value, onChange, placeholder, type = 'text', id, dark }) {
  const [focus, setFocus] = useState(false)
  const bdr = dark ? 'rgba(255,255,255,.1)' : 'rgba(10,32,40,.12)'
  return (
    <div>
      {label && (
        <label htmlFor={id} style={{ display: 'block', fontSize: 11, fontWeight: 700, color: dark ? 'rgba(255,255,255,.5)' : 'rgba(10,32,40,.5)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6, fontFamily: "'JetBrains Mono',monospace" }}>
          {label}
        </label>
      )}
      <input
        id={id} type={type} value={value} onChange={onChange} placeholder={placeholder}
        style={{
          width: '100%', padding: '12px 16px',
          background: dark ? 'rgba(255,255,255,.06)' : 'rgba(10,32,40,.05)',
          border: `1px solid ${focus ? '#0FA3B1' : bdr}`,
          borderRadius: 11, color: dark ? '#fff' : '#0A2028',
          fontFamily: "'Manrope',sans-serif", fontSize: 14,
          outline: 'none', transition: 'border-color .2s',
          boxSizing: 'border-box',
        }}
        onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
      />
    </div>
  )
}

/* ─── Password field ─── */
function PwField({ label, value, onChange, placeholder, id, dark }) {
  const [show, setShow] = useState(false)
  const [focus, setFocus] = useState(false)
  const bdr = dark ? 'rgba(255,255,255,.1)' : 'rgba(10,32,40,.12)'
  return (
    <div>
      {label && (
        <label htmlFor={id} style={{ display: 'block', fontSize: 11, fontWeight: 700, color: dark ? 'rgba(255,255,255,.5)' : 'rgba(10,32,40,.5)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6, fontFamily: "'JetBrains Mono',monospace" }}>
          {label}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        <input
          id={id} type={show ? 'text' : 'password'} value={value} onChange={onChange} placeholder={placeholder}
          autoComplete="new-password"
          style={{
            width: '100%', padding: '12px 44px 12px 16px',
            background: dark ? 'rgba(255,255,255,.06)' : 'rgba(10,32,40,.05)',
            border: `1px solid ${focus ? '#0FA3B1' : bdr}`,
            borderRadius: 11, color: dark ? '#fff' : '#0A2028',
            fontFamily: "'Manrope',sans-serif", fontSize: 14,
            outline: 'none', transition: 'border-color .2s', boxSizing: 'border-box',
          }}
          onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
        />
        <button type="button" onClick={() => setShow(s => !s)} style={{
          position: 'absolute', right: 13, top: '50%', transform: 'translateY(-50%)',
          background: 'none', border: 'none', cursor: 'pointer',
          color: dark ? 'rgba(255,255,255,.35)' : 'rgba(10,32,40,.35)', padding: 0,
        }}>
          <Ic n={show ? 'eyeoff' : 'eye'} s={15}/>
        </button>
      </div>
    </div>
  )
}

/* ─── Status box ─── */
function StatusBox({ msg, type = 'error' }) {
  if (!msg) return null
  const isErr = type === 'error'
  return (
    <div style={{
      padding: '10px 14px',
      background: isErr ? 'rgba(239,68,68,.1)' : 'rgba(15,163,177,.1)',
      border: `1px solid ${isErr ? 'rgba(239,68,68,.3)' : 'rgba(15,163,177,.3)'}`,
      borderRadius: 9,
      color: isErr ? '#EF4444' : '#0FA3B1',
      fontSize: 13,
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <Ic n={isErr ? 'info' : 'check'} s={13}/>{msg}
    </div>
  )
}

export default function ResetPasswordPage() {
  const { dark, toggle } = useTheme()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const token = String(searchParams.get('token') || '').trim()
  const emailFromUrl = String(searchParams.get('email') || '').trim().toLowerCase()
  const hasToken = Boolean(token && emailFromUrl)

  const [email, setEmail] = useState(emailFromUrl)
  const [newPass, setNewPass] = useState('')
  const [confirmPass, setConfirmPass] = useState('')
  const [loading, setLoading] = useState(false)
  const [verifyState, setVerifyState] = useState(hasToken ? 'checking' : 'idle')
  const [err, setErr] = useState('')
  const [success, setSuccess] = useState('')

  /* Verify token from URL */
  useEffect(() => {
    if (!hasToken) return
    let alive = true;
    (async () => {
      try {
        const res = await fetch(apiUrl('/api/auth/reset-password/verify'), {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, email: emailFromUrl }),
        })
        const data = await res.json()
        if (!alive) return
        if (!res.ok) { setVerifyState('invalid'); setErr(data?.message || 'Ссылка недействительна'); return }
        setVerifyState('valid')
      } catch {
        if (!alive) return
        setVerifyState('invalid')
        setErr('Не удалось проверить ссылку. Попробуйте снова.')
      }
    })()
    return () => { alive = false }
  }, [hasToken, token, emailFromUrl])

  const handleRequestReset = async (e) => {
    e.preventDefault()
    setErr(''); setSuccess('')
    const norm = email.trim().toLowerCase()
    if (!norm || !EMAIL_REGEX.test(norm)) { setErr('Введите корректный email'); return }
    setLoading(true)
    try {
      const res = await fetch(apiUrl('/api/auth/forgot-password'), {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: norm }),
      })
      const data = await res.json()
      setSuccess(data?.message || 'Мы отправили ссылку для восстановления пароля на ваш email.')
    } catch {
      setErr('Ошибка соединения с сервером. Попробуйте позже.')
    } finally {
      setLoading(false)
    }
  }

  const handleSetNewPass = async (e) => {
    e.preventDefault()
    setErr(''); setSuccess('')
    if (!newPass || !confirmPass) { setErr('Заполните оба поля'); return }
    if (newPass.length < 6) { setErr('Пароль минимум 6 символов'); return }
    if (newPass !== confirmPass) { setErr('Пароли не совпадают'); return }
    setLoading(true)
    try {
      const res = await fetch(apiUrl('/api/auth/reset-password'), {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email: emailFromUrl, newPassword: newPass, confirmPassword: confirmPass }),
      })
      const data = await res.json()
      if (!res.ok) { setErr(data?.message || 'Не удалось обновить пароль'); return }
      setSuccess('Пароль успешно обновлён! Теперь можно войти.')
      setNewPass(''); setConfirmPass('')
    } catch {
      setErr('Ошибка соединения с сервером. Попробуйте позже.')
    } finally {
      setLoading(false)
    }
  }

  const bg = dark ? '#010810' : '#F0F5F9'
  const card = dark ? '#05111F' : '#FFFFFF'
  const line = dark ? 'rgba(255,255,255,.1)' : 'rgba(10,32,40,.1)'
  const text = dark ? 'rgba(255,255,255,.85)' : 'rgba(10,32,40,.9)'
  const muted = dark ? 'rgba(255,255,255,.42)' : 'rgba(10,32,40,.48)'

  return (
    <div style={{
      minHeight: '100vh', backgroundColor: bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, fontFamily: "'Manrope',system-ui,sans-serif",
      WebkitFontSmoothing: 'antialiased',
      backgroundImage: dark
        ? 'radial-gradient(ellipse 80% 60% at 50% 0%,rgba(15,163,177,.14) 0%,transparent 70%),linear-gradient(rgba(31,200,214,.055) 1px,transparent 1px),linear-gradient(90deg,rgba(31,200,214,.055) 1px,transparent 1px)'
        : 'radial-gradient(ellipse 80% 60% at 50% 0%,rgba(15,163,177,.09) 0%,transparent 70%),linear-gradient(rgba(10,123,134,.07) 1px,transparent 1px),linear-gradient(90deg,rgba(10,123,134,.07) 1px,transparent 1px)',
      backgroundSize: 'auto,54px 54px,54px 54px',
    }}>
      <div style={{ width: '100%', maxWidth: 480, position: 'relative' }}>

        {/* Theme toggle */}
        <button onClick={toggle} style={{
          position: 'absolute', top: -48, right: 0,
          background: dark ? 'rgba(255,255,255,.07)' : 'rgba(10,32,40,.07)',
          border: `1px solid ${line}`, borderRadius: 999,
          padding: '6px 14px', color: text, fontSize: 12.5, fontWeight: 600,
          cursor: 'pointer', fontFamily: "'Manrope',sans-serif",
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <Ic n={dark ? 'sun' : 'moon'} s={14}/>{dark ? 'Светлая тема' : 'Тёмная тема'}
        </button>

        {/* Card */}
        <div style={{
          background: card, borderRadius: 22, border: `1px solid ${line}`,
          boxShadow: dark ? '0 28px 80px rgba(0,0,0,.65)' : '0 8px 40px rgba(10,32,40,.1)',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{ padding: '36px 36px 0', position: 'relative' }}>
            <button onClick={() => navigate('/auth')} style={{
              position: 'absolute', top: 18, right: 18,
              width: 32, height: 32, borderRadius: 9,
              background: dark ? 'rgba(255,255,255,.07)' : 'rgba(10,32,40,.06)',
              border: `1px solid ${line}`, color: muted,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all .2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = text }}
            onMouseLeave={e => { e.currentTarget.style.color = muted }}>
              <Ic n="x" s={14}/>
            </button>

            {/* Logo */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 11, marginBottom: 20 }}>
              <img src="/logo.png" alt="Welland" style={{ width: 46, height: 46, objectFit: 'contain', flexShrink: 0 }}/>
              <span style={{ fontFamily: 'var(--font-main)', display: 'inline-flex', alignItems: 'baseline', gap: 7 }}>
                <span style={{ fontWeight: 900, fontSize: 19, letterSpacing: '0.06em', color: dark ? '#FFFFFF' : '#0A2028' }}>WELLAND</span>
                <span style={{ fontWeight: 600, fontSize: 13, letterSpacing: '0.18em', color: '#0FA3B1' }}>CREATE</span>
              </span>
            </div>

            {hasToken ? (
              <div style={{ marginBottom: 4 }}>
                <h1 style={{ fontSize: 22, fontWeight: 800, color: text, margin: '0 0 6px', textAlign: 'center' }}>
                  Новый пароль
                </h1>
                <p style={{ fontSize: 13.5, color: muted, textAlign: 'center', margin: 0 }}>
                  Придумайте надёжный пароль для вашего аккаунта
                </p>
              </div>
            ) : (
              <div style={{ marginBottom: 4 }}>
                <h1 style={{ fontSize: 22, fontWeight: 800, color: text, margin: '0 0 6px', textAlign: 'center' }}>
                  Восстановление пароля
                </h1>
                <p style={{ fontSize: 13.5, color: muted, textAlign: 'center', margin: 0 }}>
                  Введите email - мы отправим ссылку для сброса пароля
                </p>
              </div>
            )}
          </div>

          {/* Body */}
          <div style={{ padding: '24px 36px 36px', display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* Request reset form */}
            {!hasToken && (
              success ? (
                <>
                  <StatusBox msg={success} type="success"/>
                  <p style={{ fontSize: 13, color: muted, textAlign: 'center', margin: 0, lineHeight: 1.6 }}>
                    Проверьте почту - письмо со ссылкой придёт в течение нескольких минут. Если не видите - проверьте папку «Спам».
                  </p>
                  <button onClick={() => navigate('/auth')} style={{
                    width: '100%', padding: '13px', borderRadius: 12, border: 'none',
                    background: 'linear-gradient(135deg,#1FC8D6,#0A7B86)',
                    color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer',
                    fontFamily: "'Manrope',sans-serif", boxShadow: '0 6px 20px rgba(15,163,177,.32)',
                  }}>
                    Вернуться ко входу
                  </button>
                </>
              ) : (
                <form onSubmit={handleRequestReset} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <StatusBox msg={err} type="error"/>
                  <Field value={email} onChange={e => setEmail(e.target.value)} placeholder="Ваш email адрес" type="email" id="reset-email" dark={dark}/>
                  <button type="submit" disabled={loading} style={{
                    width: '100%', padding: '13px', borderRadius: 12, border: 'none',
                    background: 'linear-gradient(135deg,#1FC8D6,#0A7B86)',
                    color: '#fff', fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                    fontFamily: "'Manrope',sans-serif", opacity: loading ? .65 : 1,
                    boxShadow: '0 6px 20px rgba(15,163,177,.32)', marginTop: 2,
                  }}>
                    {loading ? 'Отправляем...' : 'Отправить ссылку'}
                  </button>
                </form>
              )
            )}

            {/* Token verification / set new password */}
            {hasToken && (
              verifyState === 'checking' ? (
                <div style={{ textAlign: 'center', padding: '16px 0', color: muted, fontSize: 14 }}>
                  <div style={{ marginBottom: 10 }}>
                    <Ic n="loader" s={20}/>
                  </div>
                  Проверяем ссылку...
                </div>
              ) : verifyState === 'invalid' ? (
                <>
                  <StatusBox msg={err || 'Ссылка недействительна или истекла'} type="error"/>
                  <p style={{ fontSize: 13, color: muted, textAlign: 'center', margin: 0 }}>
                    Ссылка действует 30 минут. Запросите новую.
                  </p>
                  <button onClick={() => navigate('/reset-password')} style={{
                    width: '100%', padding: '13px', borderRadius: 12, border: 'none',
                    background: 'linear-gradient(135deg,#1FC8D6,#0A7B86)',
                    color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer',
                    fontFamily: "'Manrope',sans-serif", boxShadow: '0 6px 20px rgba(15,163,177,.32)',
                  }}>
                    Запросить новую ссылку
                  </button>
                </>
              ) : success ? (
                <>
                  <StatusBox msg={success} type="success"/>
                  <button onClick={() => navigate('/auth')} style={{
                    width: '100%', padding: '13px', borderRadius: 12, border: 'none',
                    background: 'linear-gradient(135deg,#1FC8D6,#0A7B86)',
                    color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer',
                    fontFamily: "'Manrope',sans-serif", boxShadow: '0 6px 20px rgba(15,163,177,.32)',
                  }}>
                    Войти в аккаунт
                  </button>
                </>
              ) : (
                <form onSubmit={handleSetNewPass} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <StatusBox msg={err} type="error"/>
                  <PwField value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="Новый пароль (мин. 6 символов)" id="new-pass" dark={dark}/>
                  <PwField value={confirmPass} onChange={e => setConfirmPass(e.target.value)} placeholder="Повторите пароль" id="confirm-pass" dark={dark}/>
                  <button type="submit" disabled={loading} style={{
                    width: '100%', padding: '13px', borderRadius: 12, border: 'none',
                    background: 'linear-gradient(135deg,#1FC8D6,#0A7B86)',
                    color: '#fff', fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                    fontFamily: "'Manrope',sans-serif", opacity: loading ? .65 : 1,
                    boxShadow: '0 6px 20px rgba(15,163,177,.32)', marginTop: 2,
                  }}>
                    {loading ? 'Сохраняем...' : 'Сохранить новый пароль'}
                  </button>
                </form>
              )
            )}

            {/* Back to login */}
            {!(success && !hasToken) && (
              <div style={{ textAlign: 'center', marginTop: 2 }}>
                <span style={{ fontSize: 13, color: muted }}>Вспомнили пароль? </span>
                <button onClick={() => navigate('/auth')} style={{
                  background: 'none', border: 'none', color: '#0FA3B1',
                  fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  fontFamily: "'Manrope',sans-serif", padding: 0,
                }}>
                  Войти
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
